import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideUndo2,
  lucideRefreshCw,
  lucideCheck,
  lucideX,
  lucideTriangleAlert,
  lucideClipboard,
  lucideLink,
} from '@ng-icons/lucide';

import { UI, type UiBadgeVariant } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';
import {
  JournalService,
  type JournalEntry,
  type LedgerLine,
} from '../../services/journal.service';

/**
 * Journal entry detail page showing all ledger lines, metadata, and reverse action.
 */
@Component({
  selector: 'app-journal-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, NgIcon, ...UI],
  templateUrl: './journal-detail.component.html',
  viewProviders: [
    provideIcons({
      lucideUndo2,
      lucideRefreshCw,
      lucideCheck,
      lucideX,
      lucideTriangleAlert,
      lucideClipboard,
      lucideLink,
    }),
  ],
})
export class JournalDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly journalService = inject(JournalService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  readonly shellRoutes = inject(ShellRoutesService);

  readonly entry = signal<JournalEntry | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  readonly reverseModalOpen = signal(false);
  readonly reversing = signal(false);
  readonly reverseError = signal<string | null>(null);
  readonly reverseSuccess = signal<string | null>(null);

  readonly copyToast = signal(false);
  private copyToastTimeout: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    if (!this.id) {
      this.loading.set(false);
      this.error.set('Missing journal entry ID.');
      return;
    }
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.journalService.getJournalEntry(this.id).subscribe({
      next: (data) => {
        this.entry.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message ?? 'Failed to load journal entry.');
        this.loading.set(false);
      },
    });
  }

  get entityKey(): string {
    return this.entityConfig?.key ?? 'journal-entries';
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityKey));
  }

  journalLinkId(id: string | number | undefined | null): string {
    return id == null || id === '' ? '' : String(id);
  }

  // ─── Ledger line helpers ───────────────────────────────────

  getDebitLines(): LedgerLine[] {
    return this.entry()?.ledger_lines?.filter((l) => l.side === 'DEBIT') ?? [];
  }

  getCreditLines(): LedgerLine[] {
    return this.entry()?.ledger_lines?.filter((l) => l.side === 'CREDIT') ?? [];
  }

  getDebitCurrencies(): string[] {
    const lines = this.getDebitLines();
    return [...new Set(lines.map((l) => l.currency_code))];
  }

  getCreditCurrencies(): string[] {
    const lines = this.getCreditLines();
    return [...new Set(lines.map((l) => l.currency_code))];
  }

  getDebitTotal(currency: string): string {
    const total = this.getDebitLines()
      .filter((l) => l.currency_code === currency)
      .reduce((sum, l) => sum + parseFloat(l.amount), 0);
    return total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  getCreditTotal(currency: string): string {
    const total = this.getCreditLines()
      .filter((l) => l.currency_code === currency)
      .reduce((sum, l) => sum + parseFloat(l.amount), 0);
    return total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  // ─── Formatting ────────────────────────────────────────────

  formatDate(value: string): string {
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return (
        d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) +
        ' at ' +
        d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      );
    } catch {
      return value;
    }
  }

  formatAmount(value: string): string {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  getEventTypeVariant(eventType: string): UiBadgeVariant {
    const variants: Record<string, UiBadgeVariant> = {
      INITIATION: 'info',
      CONFIRMATION: 'success',
      REVERSAL: 'warning',
      REFUND: 'warning',
      ADJUSTMENT: 'brand',
      SETTLEMENT: 'brand',
    };
    return variants[eventType] ?? 'neutral';
  }

  // ─── Reverse ───────────────────────────────────────────────

  openReverseModal(): void {
    this.reverseError.set(null);
    this.reverseSuccess.set(null);
    this.reverseModalOpen.set(true);
  }

  closeReverseModal(): void {
    this.reverseModalOpen.set(false);
  }

  confirmReverse(): void {
    const e = this.entry();
    if (!e) return;

    this.reversing.set(true);
    this.reverseError.set(null);

    this.journalService.reverseJournalEntry(e.id).subscribe({
      next: (res) => {
        this.reversing.set(false);
        this.reverseSuccess.set(res.message ?? 'Journal entry reversed successfully.');
        this.reverseModalOpen.set(false);
        // Reload to show updated state
        this.load();
      },
      error: (err) => {
        this.reversing.set(false);
        const detail = err?.error?.error ?? err?.error?.detail ?? err?.message ?? 'Reversal failed.';
        this.reverseError.set(typeof detail === 'string' ? detail : JSON.stringify(detail));
      },
    });
  }

  // ─── Clipboard ─────────────────────────────────────────────

  copyToClipboard(value: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      if (this.copyToastTimeout) clearTimeout(this.copyToastTimeout);
      this.copyToast.set(true);
      this.copyToastTimeout = setTimeout(() => this.copyToast.set(false), 1500);
    });
  }

  // ─── Metadata helpers ──────────────────────────────────────

  metadataKeys(): string[] {
    const e = this.entry();
    if (!e?.metadata || typeof e.metadata !== 'object') return [];
    return Object.keys(e.metadata);
  }

  metadataValue(key: string): string {
    const e = this.entry();
    if (!e?.metadata) return '';
    const val = e.metadata[key];
    return typeof val === 'string' ? val : JSON.stringify(val);
  }
}
